// WorkOrderDetailsModal.tsx
import React from 'react';
import {
  Box,
  Divider,
  IconButton,
  Modal,
  Typography,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutlineOutlined,
  EditOutlined,
} from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { currencySymbolMap } from '../../../constants/constants';
import type { ProcessedWorkOrder } from '../../../helpers/formatters';
import ComponentsTable from './ComponentsTable';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontSize={'14px'} fontWeight="medium">
      {value ?? '-'}
    </Typography>
  </Box>
);

const StatusChip = ({ label }: { label: string }) => {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: '24px',
        fontSize: '14px',
        borderRadius: '8px',
      }}
    />
  );
};

interface WorkOrderDetailsModalProps {
  open: boolean;
  data: ProcessedWorkOrder;
  onClose: () => void;
  handleEditClick: (data: ProcessedWorkOrder) => void;
  handleDeleteClick: (
    rowData: ProcessedWorkOrder,
    bulkData: null,
    dataType: 'row' | 'bulk'
  ) => void;
}

const WorkOrderDetailsModal: React.FC<WorkOrderDetailsModalProps> = ({
  open,
  data,
  onClose,
  handleEditClick,
  handleDeleteClick,
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { timezone, preferredTimeDisplayMode } = useTimeDisplayMode();

  const totalsByCurrency: Record<
    string,
    { parts: number; labor: number; total: number }
  > = {};

  (data?.components ?? []).forEach((c) => {
    const code = c?.currencyCode ?? 'DEFAULT';
    const parts = Number(c?.partsCost ?? 0) || 0;
    const labor = Number(c?.laborCost ?? 0) || 0;
    const tot = Number(c?.totalCost ?? 0) || 0;

    if (!totalsByCurrency[code]) {
      totalsByCurrency[code] = { parts: 0, labor: 0, total: 0 };
    }
    totalsByCurrency[code].parts += parts;
    totalsByCurrency[code].labor += labor;
    totalsByCurrency[code].total += tot;
  });

  const formatAmount = (n: number) =>
    n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 1100,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          outline: 'none',
          color: 'text.primary',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            gap={2}
          >
            <Typography variant="h6" component="h2">
              {t('work_order_details')}
            </Typography>
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <IconButton
                size="small"
                disabled={data?.status?.toLowerCase() === 'completed'}
                onClick={() => {
                  if (data) {
                    handleEditClick(data);
                  }
                }}
                sx={{
                  border: `1px solid ${palette.primary}`,
                  borderRadius: '8px',
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  if (data) {
                    handleDeleteClick(data, null, 'row');
                  }
                }}
                sx={{
                  border: '1px solid #EB5757',
                  borderRadius: '8px',
                }}
              >
                <DeleteOutlineOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Body */}
        <Box
          sx={{ p: 3, maxHeight: '80vh', overflowY: 'auto' }}
          display={'flex'}
          flexDirection={'column'}
          gap={2}
        >
          {/* Top details grid */}
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 3, columnGap: 3 }}
          >
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('date_created')}
                value={
                  data?.createdAt
                    ? convertUtcToDisplayTime(
                        data?.createdAt,
                        preferredTimeDisplayMode,
                        timezone,
                        DATE_TIME_FORMAT
                      )
                    : '-'
                }
              />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem label={t('asset_id')} value={data?.bumperNumber} />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem label={t('asset_type')} value={data?.assetType} />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('make_model')}
                value={`${data?.make ?? '-'}-${data?.model ?? '-'}`}
              />
            </Box>
          </Box>

          {/* Second row */}
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 3, columnGap: 3 }}
          >
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('priority')}
                value={<StatusChip label={data?.priority ?? '-'} />}
              />
            </Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('status')}
                value={<StatusChip label={data?.status ?? '-'} />}
              />
            </Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('technician_assigned')}
                value={data?.assignedUserName}
              />
            </Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('maintainable_type')}
                value={data?.maintenanceType}
              />
            </Box>
          </Box>

          {/* Costs */}
          <Box sx={{ width: { xs: '100%', md: '100%' }, mt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>
              Costs
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
                alignItems: 'start',
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('wo_overall_part_cost')}
                </Typography>
                <Typography
                  variant="body1"
                  fontSize="14px"
                  fontWeight={700}
                  mt={0.5}
                >
                  {(() => {
                    const keys = Object.keys(totalsByCurrency);
                    if (keys.length === 0) return '-';
                    if (keys.length === 1) {
                      const code = keys[0];
                      const symbol = currencySymbolMap[code] ?? '$';
                      return `${symbol} ${formatAmount(
                        totalsByCurrency[code].parts
                      )}`;
                    }
                    return keys
                      .map(
                        (code) =>
                          `${currencySymbolMap[code] ?? '$'} ${formatAmount(
                            totalsByCurrency[code].parts
                          )} (${code})`
                      )
                      .join('\n');
                  })()}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('wo_overall_labor_cost')}
                </Typography>
                <Typography
                  variant="body1"
                  fontSize="14px"
                  fontWeight={700}
                  mt={0.5}
                >
                  {(() => {
                    const keys = Object.keys(totalsByCurrency);
                    if (keys.length === 0) return '-';
                    if (keys.length === 1) {
                      const code = keys[0];
                      const symbol = currencySymbolMap[code] ?? '$';
                      return `${symbol} ${formatAmount(
                        totalsByCurrency[code].labor
                      )}`;
                    }
                    return keys
                      .map(
                        (code) =>
                          `${currencySymbolMap[code] ?? '$'} ${formatAmount(
                            totalsByCurrency[code].labor
                          )} (${code})`
                      )
                      .join('\n');
                  })()}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('wo_overall_total_cost')}
                </Typography>
                <Typography
                  variant="body1"
                  fontSize="14px"
                  fontWeight={700}
                  mt={0.5}
                >
                  {(() => {
                    const keys = Object.keys(totalsByCurrency);
                    if (keys.length === 0) return '-';
                    if (keys.length === 1) {
                      const code = keys[0];
                      const symbol = currencySymbolMap[code] ?? '$';
                      return `${symbol} ${formatAmount(
                        totalsByCurrency[code].total
                      )}`;
                    }
                    return keys
                      .map(
                        (code) =>
                          `${currencySymbolMap[code] ?? '$'} ${formatAmount(
                            totalsByCurrency[code].total
                          )} (${code})`
                      )
                      .join('\n');
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Components table */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              {t('components')}
            </Typography>

            {/* Horizontal Scroll Indicator */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
                p: 1,
                backgroundColor: (theme) => theme.palette.background.default,
                borderRadius: 1,
                border: '1px solid #FFFFFF26',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize={12}
              >
                {t('scroll_horizontally_to_view_all_columns')}
              </Typography>
            </Box>

            <ComponentsTable components={data?.components} />
          </Box>

          <Divider />
        </Box>
      </Box>
    </Modal>
  );
};

export default WorkOrderDetailsModal;
